import { yupResolver } from "@hookform/resolvers/yup";
import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  InputLabel,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { Fragment, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import {
  AccountingActionCategory,
  AccountingActionLabel,
  ActionCategoryMapping,
  CUSTOMER_MULTI_SELECT_ACTIONS,
  CustomerAccountingActionAllowedTypes,
} from "@/features/chart-of-account-preferences";
import { ChartOfAccountSelect } from "@/features/chart-of-accounts";
import {
  CenteredLoading,
  CustomDatePicker,
  CustomDialogTitle,
  CustomInputField,
} from "../../ui";
import { CountryCodeSelect } from "../../ui/custom-select/country-code-select";
import { GenderSelect } from "../gender/gender-select";
import {
  DEFAULT_DIAL_CODE,
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./customer-form-constants";

const CUSTOMER_ACTIONS =
  ActionCategoryMapping[AccountingActionCategory.CUSTOMER];

export const CustomerFormDialog = ({
  defaultValues,
  onSubmit,
  onClose,
  dialogTitle,
  renderDialogActionsChildren,
  isLoading = false,
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const [tabIndex, setTabIndex] = useState(0);

  const {
    fields: phoneNumbers,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "phoneNumbers",
    keyName: "uid",
  });

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = async (formData) => {
    const accountingActionChartOfAccounts = Object.entries(
      formData.accountingActionChartOfAccounts || {},
    ).flatMap(([action, value]) => {
      if (CUSTOMER_MULTI_SELECT_ACTIONS.has(action)) {
        return (value || []).map((coa) => ({
          action,
          chartOfAccountId: coa.id,
        }));
      }
      return value ? [{ action, chartOfAccountId: value.id }] : [];
    });

    await onSubmit({
      ...formData,
      dateOfBirth: formData.dateOfBirth
        ? dayjs(formData.dateOfBirth).format("YYYY-MM-DD")
        : null,
      accountingActionChartOfAccounts,
    });
  };

  return (
    <Dialog
      component="form"
      noValidate
      fullWidth
      maxWidth="md"
      open
      onClose={handleClose}
      onSubmit={handleSubmit(onFormSubmit)}
    >
      <CustomDialogTitle
        variant="h5"
        title={dialogTitle}
        onClose={handleClose}
      />
      {isLoading ? (
        <CenteredLoading sx={{ height: "25rem" }} />
      ) : (
        <>
          <DialogContent sx={{ pt: 0 }} dividers>
            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
            >
              <Tab label="Pelanggan" />
              <Tab label="Akun" />
            </Tabs>
            <Divider sx={{ mb: 2 }} />

            {tabIndex === 0 && (
              <>
                <Stack spacing={2}>
                  <CustomInputField
                    required
                    name="name"
                    label="Nama"
                    register={register}
                    helperText={errors.name?.message}
                    error={Boolean(errors.name)}
                  />
                  <Controller
                    control={control}
                    name="gender"
                    render={({ field, fieldState }) => (
                      <GenderSelect
                        required
                        value={field.value}
                        onChange={field.onChange}
                        label="Jenis Kelamin"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field }) => (
                      <CustomDatePicker
                        value={field.value}
                        onChange={field.onChange}
                        label="Tanggal Lahir"
                        required
                        error={Boolean(errors.dateOfBirth)}
                        helperText={errors.dateOfBirth?.message}
                        maxDate={dayjs().subtract(3, "years")}
                        minDate={dayjs().subtract(120, "years")}
                      />
                    )}
                  />
                  <CustomInputField
                    name="email"
                    label="Email"
                    register={register}
                    helperText={errors.email?.message}
                    error={Boolean(errors.email)}
                  />
                </Stack>
                <Grid container rowSpacing={2} columnSpacing={3} sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12 }}>
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}>
                      <InputLabel
                        required
                        sx={{
                          fontSize: "0.875rem",
                          lineHeight: "1.125rem",
                        }}
                      >
                        Nomor Telepon
                      </InputLabel>
                      <Button
                        onClick={() =>
                          append({ dialCode: DEFAULT_DIAL_CODE, number: "" })
                        }
                      >
                        + Tambah
                      </Button>
                    </Stack>
                  </Grid>
                  {phoneNumbers.map((phoneNumber, index) => (
                    <Fragment key={phoneNumber.uid}>
                      <Grid size={{ xs: 3 }}>
                        <Controller
                          name={`phoneNumbers[${index}].dialCode`}
                          control={control}
                          render={({ field }) => (
                            <CountryCodeSelect
                              id={`phoneNumbers[${index}].dialCode`}
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 9 }}>
                        <Stack direction="row" spacing={2}>
                          <CustomInputField
                            name={`phoneNumbers[${index}].number`}
                            register={register}
                            InputProps={{
                              placeholder: "81xxxx",
                              onChange: (e) => {
                                const numericValue = e.target.value.replace(
                                  /\D/g,
                                  "",
                                );
                                e.target.value = numericValue;
                              },
                            }}
                            helperText={
                              errors.phoneNumbers?.[index]?.number?.message
                            }
                            error={Boolean(
                              errors.phoneNumbers?.[index]?.number,
                            )}
                          />
                          {phoneNumbers.length > 1 && (
                            <IconButton onClick={() => remove(index)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </Grid>
                    </Fragment>
                  ))}
                </Grid>
              </>
            )}

            {tabIndex === 1 && (
              <Stack spacing={3}>
                {CUSTOMER_ACTIONS.map((action) => {
                  const isMultiple = CUSTOMER_MULTI_SELECT_ACTIONS.has(action);
                  return (
                    <Box key={action}>
                      <Typography variant="subtitle1" gutterBottom>
                        {AccountingActionLabel[action]}
                      </Typography>
                      <Controller
                        control={control}
                        name={`accountingActionChartOfAccounts.${action}`}
                        render={({ field }) => (
                          <ChartOfAccountSelect
                            multiple={isMultiple}
                            types={CustomerAccountingActionAllowedTypes[action]}
                            value={
                              isMultiple
                                ? field.value || []
                                : field.value || null
                            }
                            onChange={(value) =>
                              field.onChange(
                                isMultiple ? value : value || null,
                              )
                            }
                            TextFieldProps={{
                              placeholder: "Cari dan pilih akun...",
                            }}
                          />
                        )}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            {renderDialogActionsChildren({ isLoading: isSubmitting })}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

CustomerFormDialog.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  renderDialogActionsChildren: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
